import React, { useEffect, useState } from 'react';
import { TrashIcon, PlusIcon } from 'lucide-react';
const timeUnitOptions = ['Day', 'Week', 'Month', 'Quarter', 'Year'];
const fieldOptions = ['Name', 'Label', 'Unit Count', 'Management Company', 'Ownership Group'];
const operatorOptions = ['Equals', 'Does Not Equal', 'Contains', 'Does Not Contain', 'After', 'Before', 'Greater than', 'Less than'];
const responsiblePartyOptions = ['On-Site Maintenance', 'Outsourced to Vendor', 'Corporate Team', 'Tenant'];
const AddObligationForm = ({
  onSubmit,
  onCancel,
  attributes
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    attributeId: '',
    location: '',
    recurringFrequency: 1,
    recurringUnit: 'Month',
    responsibleParty: 'On-Site Maintenance'
  });
  const [propertyFields, setPropertyFields] = useState([]);
  const [locationFields, setLocationFields] = useState([]);
  const [attributeConditions, setAttributeConditions] = useState([]);
  const [fieldCombination, setFieldCombination] = useState('ALL');
  const [locationCombination, setLocationCombination] = useState('ALL');
  const [attributeCombination, setAttributeCombination] = useState('ALL');
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'attributeId' ? value ? parseInt(value, 10) : value : name === 'recurringFrequency' ? parseInt(value, 10) : value
    });
  };
  const handlePropertyFieldChange = (index, field, value) => {
    const updatedFields = [...propertyFields];
    updatedFields[index] = {
      ...updatedFields[index],
      [field]: value
    };
    setPropertyFields(updatedFields);
  };
  const handleLocationFieldChange = (index, value) => {
    const updatedFields = [...locationFields];
    updatedFields[index] = {
      location: value
    };
    setLocationFields(updatedFields);
  };
  const handleAttributeConditionChange = (index, field, value) => {
    const updatedConditions = [...attributeConditions];
    updatedConditions[index] = {
      ...updatedConditions[index],
      [field]: field === 'attributeId' && value ? parseInt(value, 10) : value
    };
    setAttributeConditions(updatedConditions);
  };
  const addPropertyField = () => {
    setPropertyFields([...propertyFields, {
      field: 'Name',
      operator: 'Equals',
      value: ''
    }]);
  };
  const addLocationField = () => {
    setLocationFields([...locationFields, {
      location: ''
    }]);
  };
  const addAttributeCondition = () => {
    setAttributeConditions([...attributeConditions, {
      attributeId: '',
      operator: 'Equals',
      value: ''
    }]);
  };
  const removePropertyField = index => {
    const updatedFields = [...propertyFields];
    updatedFields.splice(index, 1);
    setPropertyFields(updatedFields);
  };
  const removeLocationField = index => {
    const updatedFields = [...locationFields];
    updatedFields.splice(index, 1);
    setLocationFields(updatedFields);
  };
  const removeAttributeCondition = index => {
    const updatedConditions = [...attributeConditions];
    updatedConditions.splice(index, 1);
    setAttributeConditions(updatedConditions);
  };
  // Get the data type of an attribute
  const getAttributeDataType = attributeId => {
    if (!attributeId) return null;
    const attribute = attributes.find(attr => attr.id === parseInt(attributeId, 10));
    return attribute ? attribute.dataType : null;
  };
  // Render input field based on attribute data type
  const renderAttributeValueInput = (attributeId, value, onChange) => {
    const dataType = getAttributeDataType(attributeId);
    if (!dataType) {
      return <input type="text" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={value} onChange={onChange} placeholder="Value" disabled data-id="element-25" />;
    }
    if (dataType.startsWith('Yes/No')) {
      return <select className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={value} onChange={onChange} data-id="element-26">
          <option value="" data-id="element-27">Select...</option>
          <option value="Yes" data-id="element-28">Yes</option>
          <option value="No" data-id="element-29">No</option>
        </select>;
    } else if (dataType.startsWith('Number')) {
      return <input type="number" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={value} onChange={onChange} placeholder="Enter number" data-id="element-30" />;
    } else if (dataType.startsWith('Date')) {
      return <input type="date" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={value} onChange={onChange} data-id="element-31" />;
    } else if (dataType.startsWith('List')) {
      // Extract list items
      const listItems = dataType.substring(6).split(',').map(item => item.trim());
      return <select className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={value} onChange={onChange} data-id="element-32">
          <option value="" data-id="element-33">Select...</option>
          {listItems.map((item, idx) => <option key={idx} value={item} data-id="element-34">
              {item}
            </option>)}
        </select>;
    } else {
      // Default to text input
      return <input type="text" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={value} onChange={onChange} placeholder="Value" data-id="element-35" />;
    }
  };
  const handleSubmit = e => {
    e.preventDefault();
    // Prepare data based on form inputs
    const submissionData = {
      ...formData,
      propertyFields: propertyFields.length > 0 ? propertyFields : null,
      fieldCombination: propertyFields.length > 1 ? fieldCombination : null,
      locationFields: locationFields.length > 0 ? locationFields.map(field => field.location).filter(Boolean) : null,
      locationCombination: locationFields.length > 1 ? locationCombination : null,
      attributeConditions: attributeConditions.length > 0 && attributeConditions[0].attributeId ? attributeConditions : null,
      attributeCombination: attributeConditions.length > 1 ? attributeCombination : null,
      recurring: `Every ${formData.recurringFrequency} ${formData.recurringUnit}${formData.recurringFrequency > 1 ? 's' : ''}`
    };
    onSubmit(submissionData);
  };
  // Generate numbers 1-365 for frequency select
  const frequencyOptions = Array.from({
    length: 365
  }, (_, i) => i + 1);
  return <form onSubmit={handleSubmit} className="space-y-4" data-id="element-36">
      <div data-id="element-37">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-38">
          Obligation Name
        </label>
        <input type="text" name="name" id="name" required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={formData.name} onChange={handleChange} placeholder="e.g., Pool Permit, Safety Inspection, etc." data-id="element-39" />
      </div>
      <div data-id="element-40">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-41">
          Description
        </label>
        <textarea name="description" id="description" rows={2} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={formData.description} onChange={handleChange} placeholder="Brief description of this obligation" data-id="element-42" />
      </div>
      <div data-id="element-43">
        <label htmlFor="responsibleParty" className="block text-sm font-medium text-gray-700" data-id="element-44">
          Responsible Party
        </label>
        <select name="responsibleParty" id="responsibleParty" className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={formData.responsibleParty} onChange={handleChange} data-id="element-45">
          {responsiblePartyOptions.map(option => <option key={option} value={option} data-id="element-46">
              {option}
            </option>)}
        </select>
      </div>
      <div className="pt-3" data-id="element-47">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-48">
          Which Properties have this obligation?
        </h3>
        <div className="space-y-6" data-id="element-49">
          <div data-id="element-50">
            <h4 className="text-md font-medium text-gray-700 mb-2" data-id="element-51">
              Property Attribute
            </h4>
            <div className="space-y-3" data-id="element-52">
              {attributeConditions.length > 0 ? <>
                  {attributeConditions.map((condition, index) => <div key={index} className="flex items-center space-x-2" data-id="element-53">
                      <select className="block w-1/3 bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={condition.attributeId} onChange={e => handleAttributeConditionChange(index, 'attributeId', e.target.value)} data-id="element-54">
                        <option value="" data-id="element-55">Select attribute...</option>
                        {attributes.map(attr => <option key={attr.id} value={attr.id} data-id="element-56">
                            {attr.name}
                          </option>)}
                      </select>
                      <select className="block w-2/5 bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={condition.operator} onChange={e => handleAttributeConditionChange(index, 'operator', e.target.value)} data-id="element-57">
                        {operatorOptions.map(option => <option key={option} value={option} data-id="element-58">
                            {option}
                          </option>)}
                      </select>
                      {renderAttributeValueInput(condition.attributeId, condition.value, e => handleAttributeConditionChange(index, 'value', e.target.value))}
                      <button type="button" onClick={() => removeAttributeCondition(index)} className="text-gray-400 hover:text-red-500 focus:outline-none" data-id="element-59">
                        <TrashIcon className="h-5 w-5" data-id="element-60" />
                      </button>
                    </div>)}
                  {attributeConditions.length > 1 && <div className="flex items-center mt-2" data-id="element-61">
                      <select className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={attributeCombination} onChange={e => setAttributeCombination(e.target.value)} data-id="element-62">
                        <option value="ALL" data-id="element-63">ALL of the above attributes</option>
                        <option value="ANY" data-id="element-64">ANY of the above attributes</option>
                      </select>
                    </div>}
                </> : <p className="text-sm text-gray-500 italic" data-id="element-65">
                  No attribute conditions added.
                </p>}
              <div className="pt-2" data-id="element-66">
                <button type="button" onClick={addAttributeCondition} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-67">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-68" />
                  Add Attribute Condition
                </button>
              </div>
            </div>
          </div>
          <div data-id="element-69">
            <h4 className="text-md font-medium text-gray-700 mb-2" data-id="element-70">
              Geographic Location
            </h4>
            <div className="space-y-3" data-id="element-71">
              {locationFields.length > 0 ? <>
                  {locationFields.map((field, index) => <div key={index} className="flex items-center space-x-2" data-id="element-72">
                      <input type="text" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={field.location} onChange={e => handleLocationFieldChange(index, e.target.value)} placeholder="e.g., New York City, California, etc." data-id="element-73" />
                      <button type="button" onClick={() => removeLocationField(index)} className="text-gray-400 hover:text-red-500 focus:outline-none" data-id="element-74">
                        <TrashIcon className="h-5 w-5" data-id="element-75" />
                      </button>
                    </div>)}
                  {locationFields.length > 1 && <div className="flex items-center mt-2" data-id="element-76">
                      <select className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={locationCombination} onChange={e => setLocationCombination(e.target.value)} data-id="element-77">
                        <option value="ALL" data-id="element-78">ALL of the above locations</option>
                        <option value="ANY" data-id="element-79">ANY of the above locations</option>
                      </select>
                    </div>}
                </> : <p className="text-sm text-gray-500 italic" data-id="element-80">
                  No locations added.
                </p>}
              <div className="pt-2" data-id="element-81">
                <button type="button" onClick={addLocationField} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-82">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-83" />
                  Add Location
                </button>
              </div>
            </div>
          </div>
          <div data-id="element-84">
            <h4 className="text-md font-medium text-gray-700 mb-2" data-id="element-85">
              Property Field(s)
            </h4>
            <div className="space-y-3" data-id="element-86">
              {propertyFields.length > 0 ? <>
                  {propertyFields.map((field, index) => <div key={index} className="flex items-center space-x-2" data-id="element-87">
                      <select className="block w-1/3 bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={field.field} onChange={e => handlePropertyFieldChange(index, 'field', e.target.value)} data-id="element-88">
                        {fieldOptions.map(option => <option key={option} value={option} data-id="element-89">
                            {option}
                          </option>)}
                      </select>
                      <select className="block w-2/5 bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={field.operator} onChange={e => handlePropertyFieldChange(index, 'operator', e.target.value)} data-id="element-90">
                        {operatorOptions.map(option => <option key={option} value={option} data-id="element-91">
                            {option}
                          </option>)}
                      </select>
                      <input type="text" className="block w-1/4 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={field.value} onChange={e => handlePropertyFieldChange(index, 'value', e.target.value)} placeholder="Value" data-id="element-92" />
                      <button type="button" onClick={() => removePropertyField(index)} className="text-gray-400 hover:text-red-500 focus:outline-none" data-id="element-93">
                        <TrashIcon className="h-5 w-5" data-id="element-94" />
                      </button>
                    </div>)}
                  {propertyFields.length > 1 && <div className="flex items-center mt-2" data-id="element-95">
                      <select className="block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={fieldCombination} onChange={e => setFieldCombination(e.target.value)} data-id="element-96">
                        <option value="ALL" data-id="element-97">ALL of the above</option>
                        <option value="ANY" data-id="element-98">ANY of the above</option>
                      </select>
                    </div>}
                </> : <p className="text-sm text-gray-500 italic" data-id="element-99">
                  No property fields added.
                </p>}
              <div className="pt-2" data-id="element-100">
                <button type="button" onClick={addPropertyField} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-101">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-102" />
                  Add Field Criteria
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="pt-3" data-id="element-103">
        <label htmlFor="recurringFrequency" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-104">
          Recurring
        </label>
        <div className="flex items-center space-x-2" data-id="element-105">
          <span className="text-sm text-gray-500" data-id="element-106">Every</span>
          <select name="recurringFrequency" id="recurringFrequency" className="block bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={formData.recurringFrequency} onChange={handleChange} data-id="element-107">
            {frequencyOptions.map(num => <option key={num} value={num} data-id="element-108">
                {num}
              </option>)}
          </select>
          <select name="recurringUnit" id="recurringUnit" className="block bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={formData.recurringUnit} onChange={handleChange} data-id="element-109">
            {timeUnitOptions.map(unit => <option key={unit} value={unit} data-id="element-110">
                {unit}
                {formData.recurringFrequency > 1 ? 's' : ''}
              </option>)}
          </select>
        </div>
      </div>
      <div className="flex justify-end space-x-3 pt-4" data-id="element-111">
        <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-112">
          Cancel
        </button>
        <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-opacity" style={{
        backgroundColor: '#332275'
      }} data-id="element-113">
          Add Requirement
        </button>
      </div>
    </form>;
};
export default AddObligationForm;